import { Controller } from '@hotwired/stimulus'
import { DeclarativeWebPushManager } from '../notifications/declarative_web_push'
import { DeviceDetection } from '../lib/device_detection'
import { ClassLogger } from '../lib/logging/class_logger'
import { send_notifications } from '../routes'

const logger = new ClassLogger('DeclarativeWebPushDebugController')
const log = logger.log.bind(logger)
const error = logger.error.bind(logger)

// Unicode constants for status icons (check mark, cross mark, warning sign)
const ICON_SUCCESS = '\u2705'
const ICON_ERROR = '\u274C'
const ICON_WARNING = '\u26A0\uFE0F'

/**
 * Declarative Web Push Debug Controller
 * Displays debugging information for declarative web push setup
 */
export default class extends Controller {
  static targets = [
    'vapidKey',
    'vapidSubject',
    'vapidUint8Array',
    'vapidBase64Display',
    'vapidHexDisplay',
    'vapidBase64Switcher',
    'vapidHexSwitcher',
    'iOSVersion',
    'isPWA',
    'shouldInitialize',
    'sendButton',
    'testStatus',
    'subscribeButton',
    'unsubscribeButton',
    'unsubscribeAllButton',
    'uninstallServiceWorkerButton',
    'serviceWorkerStatus',
    'serviceWorkerList',
    'serviceWorkerBadge',
    'willReceive',
    'webPushTokenCount',
    'webPushAPIType',
    'refreshIcon',
    'otherServicesPanel',
    'otherServicesChevron'
  ]

  connect () {
    this.currentVapidFormat = 'base64' // Default to base64
    this.restoreStateFromURL()
    this.updateDebugInfo()
    this.spinRefreshIcon()
  }

  /**
   * Restore UI state from URL parameters
   */
  restoreStateFromURL () {
    const params = new URLSearchParams(window.location.search)

    // Restore VAPID format
    const vapidFormat = params.get('vapid')
    if (vapidFormat === 'hex') {
      this.currentVapidFormat = 'hex'
      if (this.hasVapidBase64DisplayTarget && this.hasVapidHexDisplayTarget) {
        this.vapidBase64DisplayTarget.classList.add('hidden')
        this.vapidHexDisplayTarget.classList.remove('hidden')
        if (this.hasVapidBase64SwitcherTarget) this.vapidBase64SwitcherTarget.classList.add('hidden')
        if (this.hasVapidHexSwitcherTarget) this.vapidHexSwitcherTarget.classList.remove('hidden')
      }
    }

    // Restore "Show Other Services" state
    const showOther = params.get('other')
    if (showOther === 'true' && this.hasOtherServicesPanelTarget) {
      this.otherServicesPanelTarget.classList.remove('hidden')
      if (this.hasOtherServicesChevronTarget) {
        this.otherServicesChevronTarget.style.transform = 'rotate(90deg)'
      }
      // Update service worker status when panel is restored as open
      if (this.hasServiceWorkerStatusTarget) {
        this.updateServiceWorkerStatus()
      }
    }
  }

  /**
   * Update URL with current state
   */
  updateURLState () {
    const params = new URLSearchParams(window.location.search)

    // Update VAPID format in URL
    if (this.currentVapidFormat === 'hex') {
      params.set('vapid', 'hex')
    } else {
      params.delete('vapid')
    }

    // Update "Show Other Services" state in URL
    if (this.hasOtherServicesPanelTarget && !this.otherServicesPanelTarget.classList.contains('hidden')) {
      params.set('other', 'true')
    } else {
      params.delete('other')
    }

    // Update URL without reloading page
    const newURL = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname
    window.history.replaceState({}, '', newURL)
  }

  /**
   * Toggle both APNs and Firebase panels visibility
   */
  toggleOtherServices (event) {
    event.preventDefault()
    this.otherServicesPanelTarget.classList.toggle('hidden')

    // Rotate chevron icon
    if (this.hasOtherServicesChevronTarget) {
      const chevron = this.otherServicesChevronTarget
      if (this.otherServicesPanelTarget.classList.contains('hidden')) {
        chevron.style.transform = 'rotate(0deg)'
      } else {
        chevron.style.transform = 'rotate(90deg)'
        // Update service worker status when panel is opened
        if (this.hasServiceWorkerStatusTarget) {
          this.updateServiceWorkerStatus()
        }
      }
    }

    // Persist state to URL
    this.updateURLState()
  }

  /**
   * Toggle between base64 and hex VAPID key formats
   */
  toggleVapidFormat (event) {
    event.preventDefault()

    if (this.currentVapidFormat === 'base64') {
      this.currentVapidFormat = 'hex'
      this.vapidBase64DisplayTarget.classList.add('hidden')
      this.vapidHexDisplayTarget.classList.remove('hidden')

      // Toggle switcher buttons
      if (this.hasVapidBase64SwitcherTarget) this.vapidBase64SwitcherTarget.classList.add('hidden')
      if (this.hasVapidHexSwitcherTarget) this.vapidHexSwitcherTarget.classList.remove('hidden')
    } else {
      this.currentVapidFormat = 'base64'
      this.vapidHexDisplayTarget.classList.add('hidden')
      this.vapidBase64DisplayTarget.classList.remove('hidden')

      // Toggle switcher buttons
      if (this.hasVapidHexSwitcherTarget) this.vapidHexSwitcherTarget.classList.add('hidden')
      if (this.hasVapidBase64SwitcherTarget) this.vapidBase64SwitcherTarget.classList.remove('hidden')
    }

    // Persist state to URL
    this.updateURLState()
  }

  /**
   * Refresh the page
   */
  refresh (event) {
    event.preventDefault()
    window.location.reload()
  }

  /**
   * Spin the refresh icon once when page loads
   */
  spinRefreshIcon () {
    if (this.hasRefreshIconTarget) {
      const icon = this.refreshIconTarget

      // Add spin animation class
      icon.classList.add('animate-spin-once')

      // Remove the class after animation completes (1 second)
      setTimeout(() => {
        icon.classList.remove('animate-spin-once')
      }, 1000)
    }
  }

  updateDebugInfo () {
    // Get VAPID key from meta tag
    const vapidKey = document.querySelector('meta[name="vapid-public-key"]')?.content || ''

    // Display VAPID key (base64 format)
    if (this.hasVapidKeyTarget) {
      this.vapidKeyTarget.textContent = vapidKey || '(not set)'
    }

    // Get and display VAPID subject
    const vapidSubject = document.querySelector('meta[name="vapid-subject"]')?.content || ''
    if (this.hasVapidSubjectTarget) {
      this.vapidSubjectTarget.textContent = vapidSubject || '(not set)'
    }

    // Display Uint8Array hex version
    if (this.hasVapidUint8ArrayTarget) {
      if (vapidKey) {
        try {
          const uint8Array = this.urlBase64ToUint8Array(vapidKey)
          const hexString = Array.from(uint8Array)
            .map(b => b.toString(16).padStart(2, '0'))
            .join(' ')
          this.vapidUint8ArrayTarget.textContent = hexString
        } catch (err) {
          this.vapidUint8ArrayTarget.textContent = `Error converting: ${err.message}`
        }
      } else {
        this.vapidUint8ArrayTarget.textContent = '(not set)'
      }
    }

    // Display iOS version check
    if (this.hasIOSVersionTarget) {
      const version = DeviceDetection.getIOSVersion()
      if (version) {
        this.iOSVersionTarget.textContent = version.string
        // Check if version meets requirement (iOS 18.4+)
        const meetsRequirement = version.major > 18 || (version.major === 18 && version.minor >= 4)

        this.iOSVersionTarget.classList.remove('bg-green-100', 'text-green-800', 'bg-red-100', 'text-red-800', 'bg-yellow-100', 'text-yellow-800')
        if (meetsRequirement) {
          this.iOSVersionTarget.classList.add('bg-green-100', 'text-green-800')
        } else {
          this.iOSVersionTarget.classList.add('bg-red-100', 'text-red-800')
        }
      } else {
        this.iOSVersionTarget.textContent = 'Not iOS'
        this.iOSVersionTarget.classList.remove('bg-green-100', 'text-green-800', 'bg-red-100', 'text-red-800', 'bg-yellow-100', 'text-yellow-800')
        this.iOSVersionTarget.classList.add('bg-red-100', 'text-red-800')
      }
    }

    // Display PWA check
    if (this.hasIsPWATarget) {
      const isPWAValue = DeviceDetection.isPWA()
      this.isPWATarget.textContent = isPWAValue ? 'Yes' : 'No'
      this.isPWATarget.classList.remove('bg-green-100', 'text-green-800', 'bg-red-100', 'text-red-800')
      if (isPWAValue) {
        this.isPWATarget.classList.add('bg-green-100', 'text-green-800')
      } else {
        this.isPWATarget.classList.add('bg-red-100', 'text-red-800')
      }
    }

    // Display should initialize check (iOS 18.4+ PWA)
    if (this.hasShouldInitializeTarget) {
      const shouldInit = DeclarativeWebPushManager.shouldInitialize()
      const displayText = shouldInit === true
        ? 'Yes (will initialize)'
        : `No: ${shouldInit}`
      this.shouldInitializeTarget.textContent = displayText
      this.shouldInitializeTarget.classList.remove(
        'bg-green-100',
        'text-green-800',
        'bg-red-100',
        'text-red-800'
      )
      if (shouldInit === true) {
        this.shouldInitializeTarget.classList.add('bg-green-100', 'text-green-800')
      } else {
        this.shouldInitializeTarget.classList.add('bg-red-100', 'text-red-800')
      }
    }

    // Display Web Push API Type (Declarative, Traditional, or None)
    if (this.hasWebPushAPITypeTarget) {
      const hasDeclarative = 'pushManager' in window
      const hasTraditional = 'serviceWorker' in navigator && 'PushManager' in window

      let apiType, colorClass

      if (hasDeclarative) {
        apiType = 'Declarative (iOS)'
        colorClass = 'bg-green-100 text-green-800'
      } else if (hasTraditional) {
        apiType = 'Traditional (Service Worker)'
        colorClass = 'bg-red-100 text-red-800'
      } else {
        apiType = 'None'
        colorClass = 'bg-red-100 text-red-800'
      }

      this.webPushAPITypeTarget.textContent = apiType
      this.webPushAPITypeTarget.classList.remove('bg-green-100', 'text-green-800', 'bg-red-100', 'text-red-800', 'bg-yellow-100', 'text-yellow-800')
      this.webPushAPITypeTarget.classList.add(...colorClass.split(' '))
    }

    // Display will receive push messages check (shouldInitialize AND has tokens)
    if (this.hasWillReceiveTarget) {
      const shouldInit = DeclarativeWebPushManager.shouldInitialize()
      const tokenCount = this.getWebPushTokenCount()
      const willReceive = shouldInit === true && tokenCount > 0

      this.willReceiveTarget.textContent = willReceive ? 'Yes' : 'No'
      this.willReceiveTarget.classList.remove('bg-green-100', 'text-green-800', 'bg-red-100', 'text-red-800')
      if (willReceive) {
        this.willReceiveTarget.classList.add('bg-green-100', 'text-green-800')
      } else {
        this.willReceiveTarget.classList.add('bg-red-100', 'text-red-800')
      }
    }

    // Enable/disable receive panel buttons based on shouldInitialize
    const shouldInit = DeclarativeWebPushManager.shouldInitialize()
    if (this.hasSubscribeButtonTarget) {
      this.subscribeButtonTarget.disabled = shouldInit !== true
    }
    if (this.hasUnsubscribeButtonTarget) {
      this.unsubscribeButtonTarget.disabled = shouldInit !== true
    }
    if (this.hasUnsubscribeAllButtonTarget) {
      this.unsubscribeAllButtonTarget.disabled = shouldInit !== true
    }

    // Enable/disable send button based on token count
    if (this.hasSendButtonTarget) {
      const tokenCount = this.getWebPushTokenCount()
      this.sendButtonTarget.disabled = tokenCount === 0
    }
  }

  /**
   * Update the service worker status display
   */
  async updateServiceWorkerStatus () {
    if (!this.hasServiceWorkerStatusTarget) return

    // Check if service workers are supported
    if (!('serviceWorker' in navigator)) {
      // Show badge, hide button and list
      if (this.hasServiceWorkerBadgeTarget) {
        this.serviceWorkerBadgeTarget.textContent = 'Not supported'
        this.serviceWorkerBadgeTarget.classList.remove('hidden')
      }
      if (this.hasUninstallServiceWorkerButtonTarget) {
        this.uninstallServiceWorkerButtonTarget.classList.add('hidden')
      }
      if (this.hasServiceWorkerListTarget) {
        this.serviceWorkerListTarget.textContent = ''
      }
      return
    }

    try {
      const registrations = await navigator.serviceWorker.getRegistrations()
      const count = registrations.length

      if (count === 0) {
        // Show badge, hide button and list
        if (this.hasServiceWorkerBadgeTarget) {
          this.serviceWorkerBadgeTarget.textContent = 'None installed'
          this.serviceWorkerBadgeTarget.classList.remove('hidden')
        }
        if (this.hasUninstallServiceWorkerButtonTarget) {
          this.uninstallServiceWorkerButtonTarget.classList.add('hidden')
        }
        if (this.hasServiceWorkerListTarget) {
          this.serviceWorkerListTarget.textContent = ''
        }
      } else {
        // Hide badge, show button and list
        if (this.hasServiceWorkerBadgeTarget) {
          this.serviceWorkerBadgeTarget.classList.add('hidden')
        }
        if (this.hasUninstallServiceWorkerButtonTarget) {
          this.uninstallServiceWorkerButtonTarget.classList.remove('hidden')
        }

        // Update button text
        this.serviceWorkerStatusTarget.textContent = `Uninstall (${count})`

        // Display list of service workers inline (comma-separated scopes)
        if (this.hasServiceWorkerListTarget) {
          const scopes = registrations.map(reg => {
            const url = new URL(reg.scope)
            return url.pathname === '/' ? '/' : url.pathname
          }).join(', ')

          this.serviceWorkerListTarget.textContent = scopes
          this.serviceWorkerListTarget.title = scopes // Add tooltip with full text
        }
      }
    } catch (err) {
      error('Failed to check service worker status:', err)
      // Show badge with error, hide button and list
      if (this.hasServiceWorkerBadgeTarget) {
        this.serviceWorkerBadgeTarget.textContent = 'Error'
        this.serviceWorkerBadgeTarget.classList.remove('hidden')
        this.serviceWorkerBadgeTarget.classList.add('bg-red-100', 'text-red-600')
      }
      if (this.hasUninstallServiceWorkerButtonTarget) {
        this.uninstallServiceWorkerButtonTarget.classList.add('hidden')
      }
      if (this.hasServiceWorkerListTarget) {
        this.serviceWorkerListTarget.textContent = ''
      }
    }
  }

  /**
   * Get the Web Push token count from the DOM
   * @returns {number}
   */
  getWebPushTokenCount () {
    // Get token count from the Stimulus target
    if (this.hasWebPushTokenCountTarget) {
      const count = parseInt(this.webPushTokenCountTarget.textContent.trim(), 10)
      return isNaN(count) ? 0 : count
    }
    return 0
  }

  /**
   * Send a test notification
   * @param {Event} event - Form submit event
   */
  async sendTest (event) {
    event.preventDefault()

    // Get form values
    const form = event.target
    const title = form.title.value.trim()
    const body = form.body.value.trim()
    const navigate = form['notification[navigate]'].value.trim()
    const userId = form.user_id.value.trim()

    log(`[sendTest] Form values - title: "${title}", body: "${body}", navigate: "${navigate}", user_id: "${userId}"`)

    if (!title || !body) {
      this.showTestStatus('Please provide both title and body', 'error')
      return
    }

    // Disable send button
    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = true
      this.sendButtonTarget.textContent = 'Sending...'
    }

    try {
      // Build notification payload
      const notificationPayload = {
        title,
        body
      }
      if (navigate) {
        notificationPayload.navigate = navigate
      }

      log('[sendTest] Notification payload:', notificationPayload)

      // Send notification through notifications API
      const response = await fetch(send_notifications(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        body: JSON.stringify({
          user_id: userId || null,
          notification: notificationPayload,
          token_type: 'declarative_web_push'
        })
      })

      const responseData = await response.json()

      if (response.ok && responseData.success) {
        this.showTestStatus(`${ICON_SUCCESS} ${responseData.message}`, 'success')

        // Also show a local notification if permission is granted (for immediate feedback)
        if ('Notification' in window && Notification.permission === 'granted') {
          const notification = new Notification(title, {
            body,
            icon: '/icon.png',
            tag: 'test-notification'
          })
          setTimeout(() => notification.close(), 5000)
        }
      } else {
        const errorMsg = responseData.message || responseData.error || 'Failed to send notification'
        let fullMessage = `${ICON_ERROR} ${errorMsg}`

        // Include detailed failure information if available
        if (responseData.failures && responseData.failures.length > 0) {
          fullMessage += '\n\nFailure details:\n'
          responseData.failures.forEach((failure, index) => {
            fullMessage += `\n${index + 1}. ${failure}`
          })
        }

        this.showTestStatus(fullMessage, 'error')
      }
    } catch (err) {
      error('Failed to send notification:', err)
      this.showTestStatus(`${ICON_ERROR} Failed to send: ${err.message}`, 'error')
    } finally {
      // Re-enable send button (only if there are tokens)
      if (this.hasSendButtonTarget) {
        const tokenCount = this.getWebPushTokenCount()
        this.sendButtonTarget.disabled = tokenCount === 0
        this.sendButtonTarget.textContent = 'Send to all eligible devices'
      }
    }
  }

  /**
   * Subscribe to push notifications
   */
  async subscribe () {
    const supported = DeclarativeWebPushManager.isSupported()
    if (supported !== true) {
      log(`Cannot subscribe: ${supported}`)
      alert(`Declarative Web Push not supported: ${supported}`)
      return
    }

    if (this.hasSubscribeButtonTarget) {
      this.subscribeButtonTarget.disabled = true
      this.subscribeButtonTarget.textContent = 'Subscribing...'
    }

    try {
      const subscription = await DeclarativeWebPushManager.subscribe(this.currentEcosystemId())
      if (subscription) {
        alert(`${ICON_SUCCESS} Successfully subscribed to notifications!`)
        window.location.reload()
      } else {
        alert(`${ICON_ERROR} Failed to subscribe. Please check permissions.`)
      }
    } catch (err) {
      error('Failed to subscribe:', err)
      alert(`${ICON_ERROR} Error: ${err.message}`)
    } finally {
      if (this.hasSubscribeButtonTarget) {
        this.subscribeButtonTarget.disabled = false
        this.subscribeButtonTarget.textContent = 'Subscribe to Notifications'
      }
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe () {
    const supported = DeclarativeWebPushManager.isSupported()
    if (supported !== true) {
      log(`Cannot unsubscribe: ${supported}`)
      alert(`Declarative Web Push not supported: ${supported}`)
      return
    }

    if (!confirm('Are you sure you want to unsubscribe this device from notifications?')) {
      return
    }

    if (this.hasUnsubscribeButtonTarget) {
      this.unsubscribeButtonTarget.disabled = true
      this.unsubscribeButtonTarget.textContent = 'Unsubscribing...'
    }

    try {
      await DeclarativeWebPushManager.unsubscribeFromEcosystem(this.currentEcosystemId())
      alert(`${ICON_SUCCESS} Successfully unsubscribed from notifications!`)
      window.location.reload()
    } catch (err) {
      error('Failed to unsubscribe:', err)
      alert(`${ICON_ERROR} Error: ${err.message}`)
    } finally {
      if (this.hasUnsubscribeButtonTarget) {
        this.unsubscribeButtonTarget.disabled = false
        this.unsubscribeButtonTarget.textContent = 'Unsubscribe this device'
      }
    }
  }

  /**
   * Unsubscribe all devices from push notifications
   */
  async unsubscribeAll () {
    const confirmMsg = 'Are you sure you want to unsubscribe ALL devices from ' +
                      'notifications? This will remove all registered tokens for your account.'
    if (!confirm(confirmMsg)) {
      return
    }

    if (this.hasUnsubscribeAllButtonTarget) {
      this.unsubscribeAllButtonTarget.disabled = true
      this.unsubscribeAllButtonTarget.textContent = 'Unsubscribing all...'
    }

    try {
      const message = await DeclarativeWebPushManager.unsubscribeAll()
      alert(`${ICON_SUCCESS} ${message}`)
      window.location.reload()
    } catch (err) {
      error('Failed to unsubscribe all:', err)
      alert(`${ICON_ERROR} Error: ${err.message}`)
    } finally {
      if (this.hasUnsubscribeAllButtonTarget) {
        this.unsubscribeAllButtonTarget.disabled = false
        this.unsubscribeAllButtonTarget.textContent = 'Unsubscribe all devices'
      }
    }
  }

  /**
   * Uninstall all service workers
   * Service workers are not used by Declarative Web Push, but may have been
   * installed by earlier versions of the code or by other parts of the app.
   */
  async uninstallServiceWorker () {
    if (!('serviceWorker' in navigator)) {
      alert(`${ICON_ERROR} Service Workers are not supported in this browser.`)
      return
    }

    if (this.hasUninstallServiceWorkerButtonTarget) {
      this.uninstallServiceWorkerButtonTarget.disabled = true
    }

    const originalText = this.hasServiceWorkerStatusTarget
      ? this.serviceWorkerStatusTarget.textContent
      : 'Uninstall'
    if (this.hasServiceWorkerStatusTarget) {
      this.serviceWorkerStatusTarget.textContent = 'Working...'
    }

    try {
      log('Getting service worker registrations...')
      const registrations = await navigator.serviceWorker.getRegistrations()
      log(`Found ${registrations.length} service worker(s)`)

      if (registrations.length === 0) {
        alert(`${ICON_SUCCESS} No service workers found. Nothing to uninstall.`)
        return
      }

      log('Unregistering service workers...')
      const results = await Promise.all(
        registrations.map(async (registration) => {
          const scope = registration.scope
          const success = await registration.unregister()
          log(`Unregistered ${scope}: ${success}`)
          return { scope, success }
        })
      )

      const successCount = results.filter(r => r.success).length
      const failCount = results.length - successCount

      if (failCount === 0) {
        alert(
          `${ICON_SUCCESS} Successfully uninstalled ${successCount} service worker(s).\n\n` +
          'Refresh the page to see updated status.'
        )
        setTimeout(() => window.location.reload(), 1000)
      } else {
        alert(
          `${ICON_WARNING} Uninstalled ${successCount} service worker(s), ` +
          `but ${failCount} failed.\n\nCheck the console for details.`
        )
        error('Failed to uninstall some service workers:', results.filter(r => !r.success))
        setTimeout(() => window.location.reload(), 1000)
      }
    } catch (err) {
      error('Failed to uninstall service workers:', err)
      alert(`${ICON_ERROR} Error uninstalling service workers: ${err.message}`)
    } finally {
      if (this.hasUninstallServiceWorkerButtonTarget) {
        this.uninstallServiceWorkerButtonTarget.disabled = false
      }
      if (this.hasServiceWorkerStatusTarget) {
        this.serviceWorkerStatusTarget.textContent = originalText
      }
    }
  }

  /**
   * Show status message after test send
   * @param {string} message - Status message to display
   * @param {string} type - 'success' or 'error'
   */
  showTestStatus (message, type) {
    if (!this.hasTestStatusTarget) return

    const statusEl = this.testStatusTarget
    statusEl.textContent = message
    statusEl.classList.remove(
      'hidden',
      'bg-green-50',
      'text-green-800',
      'border-green-200',
      'bg-red-50',
      'text-red-800',
      'border-red-200'
    )
    statusEl.classList.add('border')

    // Use whitespace-pre-wrap for multi-line messages
    if (message.includes('\n')) {
      statusEl.style.whiteSpace = 'pre-wrap'
    } else {
      statusEl.style.whiteSpace = 'normal'
    }

    if (type === 'success') {
      statusEl.classList.add('bg-green-50', 'text-green-800', 'border-green-200')
    } else {
      statusEl.classList.add('bg-red-50', 'text-red-800', 'border-red-200')
    }

    // Auto-hide after 5 seconds
    setTimeout(() => {
      statusEl.classList.add('hidden')
    }, 5000)
  }

  /**
   * Get current ecosystem ID from meta tag
   * @returns {string}
   */
  currentEcosystemId () {
    return document.querySelector('meta[name="ecosystem_id"]')?.content || ''
  }

  /**
   * Convert base64 VAPID key to Uint8Array
   * @param {string} base64String - Base64 encoded VAPID public key
   * @returns {Uint8Array}
   */
  urlBase64ToUint8Array (base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4)
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/')

    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
  }
}
